import { useEffect, useLayoutEffect, useMemo } from 'react';
import { useTexture } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import type { CanvasTexture } from 'three';
import type { RefObject } from 'react';
import { useCarGltf } from './gltf/carAssetLoader';
import { createWheelPivots, type WheelCornerName } from './gltf/wheelPivots';
import type { VehicleState } from '../model/vehicleDriving';
import { InteriorDisplayPanel } from './InteriorDisplayPanel';
import { SurfaceDecal } from './SurfaceDecal';
import { DECAL_IMAGE_URLS, mapDecalTextures } from './materials/decalImages';
import { SHOWROOM_LOGO_URL } from './brandAssets';
import { createDisplayTexture } from './materials/screenDisplay';
import { createPlateTexture } from './materials/licensePlate';
import {
  applyRoleMaterials,
  collectRoleMeshes,
  createGlassMaterials,
  createInteriorMaterials,
  createEmblemMaterials,
  createPaintMaterials,
  createPlateMaterials,
  createRimMaterials,
  createScreenMaterials,
  disposeMaterials,
  disposeTexturedMaterials,
  hideMeshes,
} from './gltf/applyCarConfiguration';
import type {
  CarModel as CarModelSpec,
  InteriorOption,
  PaintOption,
  Vector3Tuple,
  WheelOption,
} from '../types';

export interface CarModelProps {
  model: CarModelSpec;
  paint: PaintOption;
  wheel: WheelOption;
  interior: InteriorOption;
  /**
   * 주행 운동 상태. 바퀴 조향각과 회전각을 여기서 읽습니다.
   *
   * 왜 바퀴를 여기서 돌리는가:
   *   피벗은 로드된 씬의 내용에서 찾아내는 것이라 이 컴포넌트만 만들 수 있습니다.
   *   처음에는 그 피벗을 ref로 주행 루프(CarDriver)에 올려 보내서 거기서 돌렸는데,
   *   "만드는 곳"과 "쓰는 곳"이 3D 트리의 다른 가지로 갈리면서 둘 중 하나만 어긋나도
   *   증상이 "바퀴가 안 구른다"로만 나타나고 어디가 끊겼는지 알 수 없었습니다.
   *   피벗을 만든 컴포넌트가 직접 돌리면 그 경로가 사라집니다 — 피벗이 있으면 회전이
   *   반드시 걸립니다. 대신 이 컴포넌트가 상태를 읽어야 해서 방향이 뒤집혔습니다.
   *
   *   주행 루프보다 한 프레임 늦을 수 있지만(useFrame 순서는 마운트 순서에 달려 있습니다)
   *   16ms 지연은 바퀴 회전에서 지각되지 않습니다.
   *
   * 넘기지 않으면 바퀴는 정지 상태로 남습니다(보기 전용으로 쓸 때).
   */
  drivingStateRef?: RefObject<VehicleState>;
}

export function CarModel({ model, paint, wheel, interior, drivingStateRef }: CarModelProps) {
  const { asset } = model;
  const { scene } = useCarGltf(asset);

  /*
   * 로고는 화면이 있는 모델에서만 쓰이지만, 훅은 조건부로 호출할 수 없으므로
   * 항상 불러옵니다. 20KB짜리 파일이라 낭비가 거의 없고, 모델을 바꿔도
   * drei가 캐시해서 다시 요청하지 않습니다.
   */
  const logoTexture = useTexture(SHOWROOM_LOGO_URL);

  /*
   * 데칼 이미지는 앱 전체에서 쓰는 고정 목록을 항상 같은 순서로 불러옵니다.
   * 모델마다 개수가 다르면 훅에 넘기는 배열 길이가 바뀌어 모델 교체 시
   * 훅 순서가 흔들립니다. 자세한 이유는 materials/decalImages.ts 주석 참고.
   */
  const decalTextures = useTexture(DECAL_IMAGE_URLS);
  const decalsById = useMemo(() => mapDecalTextures(decalTextures), [decalTextures]);

  // useGLTF는 로드한 씬을 캐시해서 공유합니다.
  // 여기서는 머티리얼을 갈아끼우므로 캐시 원본을 오염시키지 않도록 복제합니다.
  // 지오메트리는 복제되지 않고 참조만 공유되므로 메모리 추가 부담은 거의 없습니다.
  const carScene = useMemo(() => scene.clone(true), [scene]);
  const roleMeshes = useMemo(() => collectRoleMeshes(carScene, model.selectors), [carScene, model.selectors]);

  // 숨김은 옵션과 무관하고 씬마다 한 번만 하면 되므로 복제 시점에 함께 처리합니다.
  useMemo(() => hideMeshes(carScene, model.hidden), [carScene, model.hidden]);

  /*
   * 바퀴를 굴리기 위한 피벗. 씬을 재부모화하는 작업이라 복제한 씬마다 한 번만 합니다.
   * 원본 캐시가 아니라 복제본을 건드리는 것이 중요합니다(위 clone 주석 참고).
   *
   * 겉모습은 바뀌지 않습니다(attach가 월드 변환을 유지합니다). 그래서 보기 모드에서도
   * 만들어 두고, 실제 회전은 주행 루프만 씁니다. 모드마다 다시 만들면 모드를 드나들 때
   * 씬 그래프를 계속 재구성하게 됩니다.
   */
  const wheelPivots = useMemo(
    () => createWheelPivots(carScene, model.bounds, asset),
    [carScene, model.bounds, asset]
  );

  /**
   * 바퀴를 상태에 맞춰 돌립니다.
   *
   * 주행 중이 아니어도 매 프레임 돕니다. 조건을 걸지 않는 이유는, 주행을 끝내면
   * 상태가 0으로 초기화되므로(CarDriver의 정리) 다음 프레임에 바퀴도 저절로 제자리로
   * 돌아오기 때문입니다. 따로 되돌리는 코드를 두면 그 둘이 어긋날 수 있습니다.
   * 회전값 여섯 개를 쓰는 비용은 무시할 수준입니다.
   */
  useFrame(() => {
    if (wheelPivots === null || drivingStateRef === undefined) {
      return;
    }
    const state = drivingStateRef.current;
    // 조향 그룹 안에 회전 그룹이 있어서, 회전축이 조향에 따라 함께 돌아갑니다.
    wheelPivots.steering.forEach((pivot) => {
      pivot.rotation.y = state.steerAngle;
    });
    wheelPivots.rolling.forEach((pivot) => {
      pivot.rotation.z = state.wheelSpin;
    });

    /*
     * 바퀴별 서스펜션 스트로크.
     *
     * 차체(bodyTilt 그룹)는 네 바퀴 평균만큼 올라갑니다. 바퀴는 그 그룹의 자식이라
     * 아무것도 하지 않으면 차체와 함께 떠서 접지면을 벗어납니다. 그래서 각 바퀴를
     * `스트로크 − 평균`만큼 되돌립니다. 평균의 합이 0이라 바퀴는 노면에 남고, 한 바퀴만
     * 요철을 밟으면 그 바퀴만 차체 안으로 들어갑니다.
     */
    (Object.keys(wheelPivots.suspension) as WheelCornerName[]).forEach((corner) => {
      const pivot = wheelPivots.suspension[corner];
      const restY = wheelPivots.suspensionRestY[corner];
      if (pivot === undefined || restY === undefined) {
        return;
      }
      pivot.position.y = restY + (state.wheelTravel[corner] - state.bodyHeave);
    });
  });

  // 옵션 종류별로 나눠 두면, 예를 들어 휠만 바꿀 때
  // 비싼 페인트 머티리얼(MeshPhysicalMaterial)이 재생성/재컴파일되지 않습니다.
  const paintMaterials = useMemo(() => createPaintMaterials(paint), [paint]);
  const glassMaterials = useMemo(() => createGlassMaterials(), []);
  const rimMaterials = useMemo(() => createRimMaterials(wheel), [wheel]);
  const interiorMaterials = useMemo(() => createInteriorMaterials(interior), [interior]);
  // 엠블럼도 트림 색을 따라가므로 내장 옵션이 바뀌면 함께 다시 만듭니다.
  const emblemMaterials = useMemo(() => createEmblemMaterials(interior), [interior]);

  /*
   * 화면은 옵션과 무관하고 모델마다 비율만 다릅니다. display 설정이 없는 모델은
   * 빈 객체가 되어 아무 메시도 건드리지 않습니다.
   */
  const screenMaterials = useMemo(
    () => (model.display ? createScreenMaterials(createDisplayTexture(logoTexture, model.display)) : {}),
    [logoTexture, model.display]
  );

  /*
   * 번호판도 화면과 같은 로고를 쓰지만 비율과 재질이 달라서 따로 굽습니다.
   * 번호판 설정이 없는 모델(에셋에 판 형상이 없는 Taycan·Model Y·M8)은 빈 객체가
   * 되어 아무 메시도 건드리지 않습니다.
   */
  const plateMaterials = useMemo(
    () => (model.plate ? createPlateMaterials(createPlateTexture(logoTexture, model.plate)) : {}),
    [logoTexture, model.plate]
  );

  /**
   * 에셋에 번호판 자리는 있지만 그 면을 따로 고를 수 없는 차량(Taycan)은 판을 얹습니다.
   *
   * 비율은 판의 실측 크기에서 그대로 뽑습니다. aspect를 매니페스트에 따로 적어 두면
   * 판 크기를 고칠 때 둘이 어긋나서 로고가 찌그러집니다(displayPanel과 같은 이유).
   */
  const platePanelTexture = useMemo<CanvasTexture | null>(() => {
    const panel = model.platePanel;
    if (panel === undefined) {
      return null;
    }
    return createPlateTexture(logoTexture, {
      aspect: panel.size[0] / panel.size[1],
      backgroundColor: panel.backgroundColor,
    });
  }, [logoTexture, model.platePanel]);

  /**
   * 에셋에 화면이 없는 차량(Taycan)은 우리가 판을 얹습니다.
   * 텍스처 비율은 판의 실측 크기에서 그대로 뽑습니다. aspect를 따로 적어 두면
   * 판 크기를 바꿀 때 둘이 어긋나서 로고가 찌그러집니다.
   */
  const panelTexture = useMemo<CanvasTexture | null>(() => {
    const panel = model.displayPanel;
    if (panel === undefined) {
      return null;
    }
    return createDisplayTexture(logoTexture, {
      aspect: panel.size[0] / panel.size[1],
      backgroundColor: panel.backgroundColor,
    });
  }, [logoTexture, model.displayPanel]);

  /**
   * 판이 향할 지점. 붙일 면의 실측 법선이 있으면 그 방향을,
   * 없으면 운전석 눈을 봅니다(자유롭게 세운 판일 때).
   */
  const panelFaceTowards = useMemo<Vector3Tuple | null>(() => {
    const panel = model.displayPanel;
    if (panel === undefined) {
      return null;
    }
    const { normal, position } = panel;
    if (normal === undefined) {
      return model.interiorEye;
    }
    return [position[0] + normal[0], position[1] + normal[1], position[2] + normal[2]];
  }, [model.displayPanel, model.interiorEye]);

  useEffect(() => () => disposeMaterials(paintMaterials), [paintMaterials]);
  useEffect(() => () => disposeMaterials(glassMaterials), [glassMaterials]);
  useEffect(() => () => disposeMaterials(rimMaterials), [rimMaterials]);
  useEffect(() => () => disposeMaterials(interiorMaterials), [interiorMaterials]);
  useEffect(() => () => disposeMaterials(emblemMaterials), [emblemMaterials]);
  useEffect(() => () => disposeTexturedMaterials(screenMaterials), [screenMaterials]);
  // 번호판 머티리얼도 캔버스 텍스처를 소유하므로 같은 경로로 해제합니다.
  useEffect(() => () => disposeTexturedMaterials(plateMaterials), [plateMaterials]);
  /*
   * 판의 머티리얼은 R3F가 정리하지만 텍스처는 우리가 만든 것이라 직접 해제해야
   * 합니다. 모델을 여러 번 바꾸면 GPU에 캔버스 텍스처가 계속 쌓입니다.
   */
  useEffect(() => () => panelTexture?.dispose(), [panelTexture]);
  // 번호판 판의 텍스처도 우리가 만든 것이라 직접 해제합니다(위 판과 같은 이유).
  useEffect(() => () => platePanelTexture?.dispose(), [platePanelTexture]);

  useLayoutEffect(() => {
    applyRoleMaterials(roleMeshes, paintMaterials);
    applyRoleMaterials(roleMeshes, glassMaterials);
    applyRoleMaterials(roleMeshes, rimMaterials);
    applyRoleMaterials(roleMeshes, interiorMaterials);
    applyRoleMaterials(roleMeshes, emblemMaterials);
    applyRoleMaterials(roleMeshes, screenMaterials);
    applyRoleMaterials(roleMeshes, plateMaterials);
  }, [
    roleMeshes,
    paintMaterials,
    glassMaterials,
    rimMaterials,
    interiorMaterials,
    emblemMaterials,
    screenMaterials,
    plateMaterials,
  ]);

  return (
    <>
      <primitive
        object={carScene}
        rotation={[0, asset.rotationY, 0]}
        scale={asset.scale}
        position={asset.position}
      />
      {/*
        판은 차량의 형제로 둡니다. 차량 하위에 넣으면 rotationY와 scale이 한 번 더
        먹어서 위치가 어긋납니다. 매니페스트 좌표는 이미 앱 좌표계 실측값입니다.
      */}
      {model.displayPanel ? (
        <InteriorDisplayPanel panel={model.displayPanel} texture={panelTexture} faceTowards={panelFaceTowards} />
      ) : null}
      {(model.decals ?? []).map((decal) => (
        <SurfaceDecal key={decal.id} decal={decal} texture={decalsById[decal.image]} />
      ))}
      {/*
        번호판 판도 데칼과 같은 컴포넌트로 그립니다. 좌표가 이미 앱 좌표계
        실측값이라 차량의 형제로 둡니다(차량 하위에 넣으면 rotationY와 scale이
        한 번 더 먹어서 위치가 어긋납니다).
      */}
      {model.platePanel ? <SurfaceDecal decal={model.platePanel} texture={platePanelTexture} /> : null}
    </>
  );
}

/*
 * 기본 모델과 Draco 디코더의 선행 로드는 gltf/carAssetLoader.ts에 있습니다.
 * 로드·preload·prefetch가 같은 로더 확장을 써야 해서 한 모듈에 모아 뒀습니다.
 */
